/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { useMutation } from '@apollo/client';
import urlJoin from 'url-join';

import config from 'js/app/config';
import logger from 'js/app/loggerSingleton';
import { useLocation, useNavigate } from 'js/lib/useRouter';
import { isAuthenticatedUser, get as userGet } from 'js/lib/user';

import { Dialog, breakpoints } from '@coursera/cds-core';
import type { EOIButtonSource, EoiSection } from '@coursera/event-pulse-types';

import * as actions from 'bundles/authentication-common/components/actions';
import { shouldShowLegalConsolidationAuthFlow } from 'bundles/authentication-common/utils';
import ConfirmationSection from 'bundles/expression-of-interest/components/ConfirmationSection';
import { useEoiOptimizationsContext } from 'bundles/expression-of-interest/components/EoiOptimizationsContext';
import InitialSection from 'bundles/expression-of-interest/components/InitialSection';
import LoginSection from 'bundles/expression-of-interest/components/LoginSection';
import PhoneNumberSection from 'bundles/expression-of-interest/components/PhoneNumberSection';
import SelectSubjectSection from 'bundles/expression-of-interest/components/SelectSubjectSection';
import { ACTION_QUERY_PARAMS, CONTINUE_EOI_PARAMS } from 'bundles/expression-of-interest/constants';
import { CREATE_OR_UPDATE } from 'bundles/expression-of-interest/queries/CreateOrUpdateQualificationSurveySectionResponse';
import type {
  CreateOrUpdateResponse,
  CreateOrUpdateVariables,
} from 'bundles/expression-of-interest/queries/CreateOrUpdateQualificationSurveySectionResponse';
import type {
  PreviewStartSectionResponse,
  PreviewStartSectionVariables,
} from 'bundles/expression-of-interest/queries/PreviewStartSection';
import { PREVIEW_START_SECTION } from 'bundles/expression-of-interest/queries/PreviewStartSection';
import type { ProfilePhone } from 'bundles/expression-of-interest/types';
import { useFocusTrap } from 'bundles/expression-of-interest/utils/dialogUtils';
import { useEoiEventing } from 'bundles/expression-of-interest/utils/eventingUtils';
import {
  appendUtmParametersToApplicationLink,
  clearActionQueryParamLocation,
  getActionQueryParam,
  getEoiContext,
  getInputfromUserProfile,
  getResponse,
  getSplitName,
} from 'bundles/expression-of-interest/utils/util';
import type { Phone } from 'bundles/naptimejs/resources/__generated__/ProfilesV1';
import useSaveOnboardingOnSignup from 'bundles/onboarding-common/hooks/useSaveOnboardingOnSignup';
import { ProductTypes } from 'bundles/university-program-qualification/utils/constants';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onOpen: () => void;
  productId: string;
  applicationUrl?: string | null;
  setHasSubmittedEoi?: (input: boolean) => void;
  initialSection?: SectionName;
  shouldNavigateDirectlyToApplication?: boolean;
  eoiButtonSource?: EOIButtonSource;
};

// Section management
export type SectionName = 'Initial' | 'PhoneNumber' | 'Login' | 'Confirmation' | 'SelectSubject';

const styles = {
  dialog: css`
    [role='dialog'] {
      max-height: 95vh;
      ${breakpoints.up('md')} {
        width: 584px;
      }

      label {
        margin-bottom: 0;
      }

      ${breakpoints.down('xs')} {
        max-height: unset;
      }
    }
  `,
};

/**
 * This component renders a dialog for the Expression of Interest (EOI) flow. It manages the state and logic required to navigate through different sections of the EOI form.
 *
 * @param {boolean} props.isOpen - Indicates whether the dialog is open.
 * @param {Function} props.onClose - Callback function to close the dialog.
 * @param {Function} props.onOpen - Callback function to open the dialog.
 * @param {string} props.productId - The ID of the product.
 * @param {string} [props.applicationUrl] - The URL for starting the application process.
 * @param {Function} [props.setHasSubmittedEoi] - Callback function to set the EOI submission state.
 * @param {string} [props.initialSection] - The initial section of the EOI dialog.
 * @param {boolean} [props.shouldNavigateDirectlyToApplication] - Indicates whether to navigate directly to the application
 * @param {boolean} [props.eoiButtonSource] - Indicates the source of the EOI interaction/submission
 * @returns {React.ReactElement} The rendered EoiDialog component.
 */
export const EoiDialog = (props: Props): React.ReactElement => {
  const {
    isOpen,
    onClose,
    onOpen,
    productId,
    applicationUrl,
    setHasSubmittedEoi,
    initialSection,
    shouldNavigateDirectlyToApplication,
    eoiButtonSource,
  } = props;
  const trackingProductId = productId.replace('Degree~base!~', '').replace('Degree~credentialTrack!~', '');
  const { slug, productType } = useEoiOptimizationsContext();
  const { saveOnboardingOnSignup } = useSaveOnboardingOnSignup();

  const isLoggedIn = isAuthenticatedUser();

  const { trackInteractEoi } = useEoiEventing(trackingProductId);

  // Router variables
  const navigate = useNavigate();
  const location = useLocation();
  const pathnameUrl = location.pathname;
  const isDegreeHubSlug = slug === 'degrees_hub';
  const usePathnameUrl = isDegreeHubSlug || !slug;
  const currentUrl = urlJoin(config.url.base, !usePathnameUrl ? `degrees/${slug}` : pathnameUrl ?? '').replace(
    /\/+$/,
    ''
  );
  const eoiContext = getEoiContext(currentUrl);

  // API calls
  const [previewStartSection, { data: previewStartSectionData }] = useMutation<
    PreviewStartSectionResponse,
    PreviewStartSectionVariables
  >(PREVIEW_START_SECTION);
  const sectionId = previewStartSectionData?.QualificationSurveySectionResponse.sectionId || 'section1';
  const [createOrUpdate] = useMutation<CreateOrUpdateResponse, CreateOrUpdateVariables>(CREATE_OR_UPDATE);
  const [responseId, setResponseId] = useState<string>('');

  // Form fields
  const currentUser = userGet();
  const defaultName = getSplitName(currentUser.fullName);
  const [firstName, setFirstName] = useState<string>(defaultName.firstName || '');
  const [lastName, setLastName] = useState<string>(defaultName.lastName || '');
  const [learnerEmail, setLearnerEmail] = useState<string>(currentUser.email_address);
  const [phone, setPhone] = useState<Phone>({ phoneNumber: '' });
  const [subject, setSubject] = useState<{ categoryId: string; categoryText: string } | undefined>();
  const [isPhoneLoading, setIsPhoneLoading] = useState<boolean>(true);

  const continueFrom = location.query.continueFrom;
  const isContinueFromLogin = continueFrom === CONTINUE_EOI_PARAMS.CONTINUE_FROM_LOGIN;

  const getInitialSection = (): SectionName => {
    if (isLoggedIn && firstName && learnerEmail) {
      if (isContinueFromLogin) {
        if (productType === ProductTypes.DEGREE_HUB) {
          return 'SelectSubject';
        }
        return 'Confirmation';
      }
      return 'PhoneNumber';
    }
    return 'Initial';
  };

  const [currentSection, setCurrentSection] = useState<SectionName>(getInitialSection());
  // Disable focus lock in the CDS Dialog when displaying the signup/login form with the Arkose/reCAPTCHA challenge modal.
  const customFocusTrapSections = ['Initial', 'Login'];
  const shouldUseCustomFocusTrap = customFocusTrapSections.includes(currentSection);

  const dialogRef = useRef<HTMLDivElement>(null);
  const isNewSignupRef = useRef<boolean>(false);

  // Trap the focus state within the dialog when the focus lock is disabled
  useFocusTrap(dialogRef, shouldUseCustomFocusTrap);

  // Open the dialog when a user comes from SSO
  useEffect(() => {
    // This can be set from an external link or from a previous login from EOI,
    const actionQueryParam = getActionQueryParam(location);
    if (actionQueryParam === ACTION_QUERY_PARAMS.EMAIL_ME_INFO) {
      onOpen();
      clearActionQueryParamLocation(location, navigate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (productId) {
      previewStartSection({ variables: { productId, input: {} } });
    }

    // Get phone number from user profile
    const fetchPhone = async () => {
      if (isLoggedIn) {
        const phoneResponse = await getInputfromUserProfile();
        let defaultPhone = { phoneNumber: '' } as Phone;
        if (phoneResponse.phoneNumber) {
          // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          defaultPhone = phoneResponse.phoneNumber['org.coursera.survey.question.ShortAnswerResponse'].text as Phone;
        }
        setPhone(defaultPhone as Phone);
        setIsPhoneLoading(false);
        setCurrentSection(getInitialSection());
      } else {
        setIsPhoneLoading(false);
        setPhone({
          phoneNumber: '',
        });
      }
    };
    fetchPhone();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const submitResponse = (): Promise<void | {}> => {
    const responses = getResponse(firstName, lastName, learnerEmail, phone.phoneNumber, subject);
    setHasSubmittedEoi?.(true);

    // TODO: For login EOI flow, this logic unintentionally skips the EOI submission
    // We should revist and add profile data fetch here
    if (!firstName || !lastName) {
      return Promise.resolve();
    }

    return createOrUpdate({
      variables: {
        productId,
        shouldProcessTerminalActions: true,
        responseId: responseId ? `&responseId=${responseId}` : '',
        sectionId,
        eoiContext,
        responses,
      },
    })
      .then((response) => {
        if (response.data) {
          setResponseId(response.data.QualificationSurveySectionResponse.id);
        }
      })
      .catch((error) => logger.error(error));
  };

  const handleClose = async (eoiSection: EoiSection) => {
    trackInteractEoi({ eoiSection, eoiInteraction: 'close_form', eoiButtonSource });

    onClose();

    // Refresh the page for newly created accounts
    if (isNewSignupRef.current) {
      await saveOnboardingOnSignup();
      actions.onSignup(undefined, { addShowTouAcceptParam: shouldShowLegalConsolidationAuthFlow() });
      isNewSignupRef.current = false;
    }
    /*
    timeout duration is 1 ms more than the transition to close the modal which is exactly 195 ms
    see duration.leavingScreen in node_modules/@material-ui/core/styles/transitions.js to see the transition duration
    the duration is set for the CDS Modal here https://github.com/webedx-spark/cds/blob/main/packages/cds-core/src/Modal/useTransitionDuration.ts#L9
    */
    if (currentSection === 'Login') {
      setTimeout(() => {
        setCurrentSection(getInitialSection());
      }, 196);
    }
  };

  const SectionEventKeys: Record<SectionName, EoiSection> = {
    Initial: 'initial_section',
    PhoneNumber: 'phone_number_section',
    Confirmation: 'confirmation_section',
    Login: 'login_section',
    // Qualifying Questions
    SelectSubject: 'select_subject_section',
  };

  const shortCircuitNextStepNavigation = (callback?: () => void) => {
    if (shouldNavigateDirectlyToApplication && applicationUrl) {
      window.open(appendUtmParametersToApplicationLink(applicationUrl), '_blank', 'noopener,noreferrer');
      handleClose(SectionEventKeys[currentSection]);
    } else {
      callback?.();
    }
  };

  const Sections: Record<SectionName, React.ReactNode> = {
    Initial: (
      <InitialSection
        handleFirstNameChange={(fName: string) => setFirstName(fName)}
        handleLastNameChange={(lName: string) => setLastName(lName)}
        handleEmailChange={(email: string) => setLearnerEmail(email)}
        handlePhoneChange={(data?: ProfilePhone) => {
          if (data?.phone) {
            const phoneNumberForEoiForm = data.phone;
            setPhone(phoneNumberForEoiForm);
          }
        }}
        firstName={firstName}
        lastName={lastName}
        learnerEmail={learnerEmail}
        phone={phone}
        handleSubmit={submitResponse}
        navigateLogin={() => setCurrentSection('Login')}
        navigateSelectSubject={() => setCurrentSection('SelectSubject')}
        navigateConfirmation={() => shortCircuitNextStepNavigation(() => setCurrentSection('Confirmation'))}
        trackingProductId={trackingProductId}
        setIsNewSignup={(userSignedUp) => {
          isNewSignupRef.current = userSignedUp;
        }}
        shouldSetAutoFocus={shouldUseCustomFocusTrap}
        eoiButtonSource={eoiButtonSource}
      />
    ),
    PhoneNumber: (
      <PhoneNumberSection
        phone={phone}
        isPhoneLoading={isPhoneLoading}
        handleSubmit={submitResponse} // an initial EOI is already captured before the phone number section
        navigateSelectSubject={() => setCurrentSection('SelectSubject')}
        navigateConfirmation={() => shortCircuitNextStepNavigation(() => setCurrentSection('Confirmation'))}
        handlePhoneChange={(data?: ProfilePhone) => {
          if (data?.phone) {
            const phoneNumberForEoiForm = data.phone;
            setPhone(phoneNumberForEoiForm);
          }
        }}
        trackingProductId={trackingProductId}
        eoiButtonSource={eoiButtonSource}
      />
    ),

    Login: (
      <LoginSection
        learnerEmail={learnerEmail}
        phone={phone}
        handleEmailChange={(email: string) => setLearnerEmail(email)}
        handlePhoneChange={(data?: ProfilePhone) => {
          if (data?.phone) {
            const phoneNumberForEoiForm = data.phone;
            setPhone(phoneNumberForEoiForm);
          }
        }}
        handlePreviousSection={() => {
          setCurrentSection('Initial');
        }}
        handleSubmit={submitResponse}
        trackingProductId={trackingProductId}
        shouldSetAutoFocus={shouldUseCustomFocusTrap}
        navigateConfirmation={shouldNavigateDirectlyToApplication ? shortCircuitNextStepNavigation : undefined}
        eoiButtonSource={eoiButtonSource}
      />
    ),
    SelectSubject: (
      <SelectSubjectSection
        subject={subject}
        handleSubjectChange={(selection: { categoryId: string; categoryText: string }) => setSubject(selection)}
        handleSubmit={submitResponse}
        navigateConfirmation={() => shortCircuitNextStepNavigation(() => setCurrentSection('Confirmation'))}
        trackingProductId={trackingProductId}
        eoiButtonSource={eoiButtonSource}
      />
    ),
    Confirmation: (
      <ConfirmationSection
        email={learnerEmail}
        applicationUrl={applicationUrl}
        handleClose={() => handleClose('confirmation_section')}
        trackingProductId={trackingProductId}
      />
    ),
  };

  return (
    <Dialog
      ref={dialogRef}
      open={isOpen}
      onClose={() => handleClose(SectionEventKeys[currentSection])}
      css={styles.dialog}
      data-testid="eoi-modal"
      disableFocusLock={shouldUseCustomFocusTrap}
    >
      {initialSection && Sections[initialSection]}
      {!initialSection && Sections[currentSection]}
    </Dialog>
  );
};

export default EoiDialog;
